import { DateTime } from '@ksyos/date-time';
import { Alert } from '@ksyos/design-system';
import { Map } from 'immutable';
import type { IBreadcrumbItem } from '@frontend/components/elements/common';
import type { IWithRouterProps } from '@frontend/components/elements/HoC/withRouter';
import type { ValidationErrors } from '@frontend/utils';
import {
    ManagerAuthorization,
    RestEcgMeterSoftwareNames,
    ServiceCode,
    ServiceType,
    SpirometerSoftwareNames,
} from '@common/enums';
import { getAnalystAllowedByServiceCode } from '@common/utils/getAnalystAllowedByServiceCode';
import { getDisplayName } from '@common/utils/getDisplayName';
import { serviceProviderAddSchema, serviceProviderEditSchema } from '@common/validation/serviceProvider';
import {
    CancelButton,
    DateInput,
    Dropdown,
    EnumDropdown,
    FormContainer,
    FormGroup,
    FormHeader,
    FormLabel,
    Header,
    InputCheckbox,
    InputGroup,
    Loading,
    OverlapMessage,
    SaveSubmit,
    UserTextParagraph,
} from '@frontend/components/elements/common';
import { Component } from '@frontend/components/elements/Component';
import { withRouter } from '@frontend/components/elements/HoC/withRouter';
import { OrganizationSelection } from '@frontend/components/elements/OrganizationSelection';
import { FixedWidthTemplate } from '@frontend/components/templates/FixedWidthTemplate';
import { fetchJson } from '@frontend/store/fetch';
import { translateKey } from '@frontend/translate';
import {
    assertHasAuthorization,
    emptyValidationErrors,
    OverlapChecker,
    RateLimiter,
    updateValidationErrorsForOne,
    validate,
} from '@frontend/utils';
import { CompensationPayoutList } from '../compensationPayouts/CompensationPayoutList';
import { ServiceOrganizationConsumerListAddEdit } from './ServiceOrganizationConsumerListAddEdit';

type ServiceProviderAddEditProps = IWithRouterProps;

interface IEmployment {
    emailAddress: string | null;
    emailAddressAlt: string | null;
    id: string;
    organization: {
        emailAddressRequestInbox: string | null;
    };
    organizationId: string;
    practitionerEmailAddress: string | null;
}

interface IOrganization {
    emailAddressRequestInbox: string | null;
    id: string;
    name: string;
}

interface IPractitioner {
    emailAddress: string | null;
    nameFamily: string;
    nameGiven: string | null;
    nameInfix: string | null;
}

interface IPractitionerEmployment {
    emailAddress: string | null;
    emailAddressAlt: string | null;
    id: string;
    organization: IOrganization;
    organizationId: string;
}

interface IOrganizationEmployment {
    emailAddress: string | null;
    emailAddressAlt: string | null;
    id: string;
    organization: IOrganization;
    practitioner: IPractitioner;
    practitionerId: string;
}

export interface IService {
    code: ServiceCode;
    id: string;
    isStandaloneGraderNotAllowed: boolean;
    name: string;
    serviceType: ServiceType;
}

export interface ISelectedOrganization {
    id: string;
    isActive: boolean;
    name: string;
}

interface IServiceProvider {
    canSelectCareProviderAsServiceConsumer: boolean;
    compensationPayoutGlaucoma: number | null;
    compensationPayoutProcedure: number | null;
    compensationPayoutReport: number | null;
    employmentId: string | null;
    endDate: string | null;
    isAnalyst: boolean;
    isBiometrician: boolean;
    isGrader: boolean;
    isGraderSelector: boolean;
    isRequestCreator: boolean;
    isRequestRecipient: boolean;
    isServiceConsumerSelector: boolean;
    preferredOrganizations: string[];
    sendNotificationEmployment: boolean;
    sendNotificationEmploymentAlt: boolean;
    sendNotificationPractitioner: boolean;
    serviceId: string | null;
    spirometerSoftwareName: SpirometerSoftwareNames | null;
    startDate: string | null;
    usingRestEcgMeter: boolean;
    usingSpirometer: boolean;
}

export type SelectedOrganizations = Map<string, ISelectedOrganization>;

interface IServiceProviderAddEditState {
    employment?: IEmployment;

    errors: ValidationErrors;
    isSaving: boolean;
    organizationEmployments: IOrganizationEmployment[];
    organizationId: string | null;

    overlap?: {
        endDate: string | null;
        id: string;
        startDate: string;
    };
    practitioner?: IPractitioner;
    practitionerEmployments: IPractitionerEmployment[];

    selectedOrganizations: SelectedOrganizations;

    service?: IService;
    serviceProvider?: IServiceProvider;
    services: IService[];

    showOrganizationNoRecipientMessage: boolean;

    showPayoutMessage: boolean;
    showServiceConsumerSelectorMessage: boolean;
}

class InnerServiceProviderAddEdit extends Component<ServiceProviderAddEditProps, IServiceProviderAddEditState> {
    private readonly atMostOncePerSecond = new RateLimiter(1000);
    private readonly overlapChecker = new OverlapChecker('/api/serviceProviders');

    constructor(props: ServiceProviderAddEditProps) {
        super(props);

        assertHasAuthorization(ManagerAuthorization.serviceRightsCareproviders);

        if (
            (!this.getQueryPractitionerId() && !this.getQueryServiceId()) ||
            (this.getQueryPractitionerId() && this.getQueryServiceId())
        ) {
            throw new Error('Incorrect practitionerId or serviceId');
        }

        assertHasAuthorization(ManagerAuthorization.serviceRightsCareproviders);

        this.state = {
            services: [],
            practitionerEmployments: [],
            organizationEmployments: [],
            selectedOrganizations: Map(),
            showPayoutMessage: false,
            showOrganizationNoRecipientMessage: false,
            showServiceConsumerSelectorMessage: false,
            organizationId: null,

            isSaving: false,
            errors: emptyValidationErrors,
        };
    }

    public async componentDidMount() {
        await this.setupComponent(this.props, this.getParamServiceProviderId());
    }

    public render() {
        const breadcrumbs: IBreadcrumbItem[] = [{ text: translateKey('serviceProvider.serviceProvider') }];
        if (this.isEdit()) {
            breadcrumbs.push({ text: translateKey('common.edit') });
        } else {
            breadcrumbs.push({ text: translateKey('common.new') });
        }
        if (this.getQueryPractitionerId() && this.state.practitioner) {
            breadcrumbs.push({
                text: getDisplayName(this.state.practitioner),
                url: `/practitioners/${this.getQueryPractitionerId()}`,
            });
        } else if (this.getQueryServiceId() && this.state.service) {
            breadcrumbs.push({
                text: this.state.service.name,
                url: `/services/${this.getQueryServiceId()}?tab=providers`,
            });
        }

        const headerText =
            `${this.isEdit() ? translateKey('common.edit') : translateKey('common.new')}` +
            ` ${translateKey('serviceProvider.serviceProvider')} ${this.state.service ? this.state.service.name : ''}`;

        return (
            <FixedWidthTemplate breadcrumb={breadcrumbs}>
                <Header content={headerText} />

                {this.getQueryPractitionerId() && (
                    <Loading
                        item={this.state.practitioner}
                        loaded={(practitioner) => (
                            <>{this.renderProviderConsumerAddEdit(practitioner, this.state.service)}</>
                        )}
                    />
                )}

                {this.getQueryServiceId() && (
                    <Loading
                        item={this.state.service}
                        loaded={(service) => (
                            <>{this.renderProviderConsumerAddEdit(this.state.practitioner, service)}</>
                        )}
                    />
                )}
            </FixedWidthTemplate>
        );
    }

    private renderProviderConsumerAddEdit(practitioner: IPractitioner | undefined, service: IService | undefined) {
        return (
            <Loading
                item={this.state.serviceProvider}
                loaded={(serviceProviderState) => (
                    <FormContainer onSubmit={async () => await this.save()}>
                        {this.state.overlap && (
                            <OverlapMessage
                                endDate={this.state.overlap.endDate}
                                link={
                                    `/serviceProviders/${this.state.overlap.id}?` +
                                    `${
                                        this.getQueryServiceId() && serviceProviderState.serviceId
                                            ? `serviceId=${this.getQueryServiceId()}`
                                            : ''
                                    }` +
                                    `${
                                        this.getQueryPractitionerId() && practitioner
                                            ? `practitionerId=${this.getQueryPractitionerId()}`
                                            : ''
                                    }` +
                                    `${
                                        this.getParamServiceProviderId()
                                            ? `&previousId=${this.getParamServiceProviderId()}`
                                            : ''
                                    }`
                                }
                                message={translateKey('serviceConsumer.overlapMessage')}
                                startDate={this.state.overlap.startDate}
                            />
                        )}

                        <FormGroup hasErrors={this.state.errors.has('startDate')}>
                            <FormLabel isRequired>{translateKey('common.startDate')}</FormLabel>
                            <InputGroup>
                                <DateInput
                                    error={this.state.errors.get('startDate')}
                                    onChange={async (value) => await this.handleChange('startDate', value)}
                                    value={serviceProviderState.startDate}
                                />
                            </InputGroup>
                        </FormGroup>

                        <FormGroup hasErrors={this.state.errors.has('endDate')}>
                            <FormLabel>{translateKey('common.endDate')}</FormLabel>
                            <InputGroup>
                                <DateInput
                                    error={this.state.errors.get('endDate')}
                                    onChange={async (value) => await this.handleChange('endDate', value)}
                                    value={serviceProviderState.endDate}
                                />
                            </InputGroup>
                        </FormGroup>

                        {!this.isEdit() && this.getQueryPractitionerId() && (
                            <>
                                <FormHeader>
                                    {translateKey('employment.employment') + ' & ' + translateKey('service.service')}
                                </FormHeader>
                                <FormGroup hasErrors={this.state.errors.has('employmentId')}>
                                    <FormLabel isRequired>{translateKey('employment.employment')}</FormLabel>
                                    <InputGroup>
                                        <Dropdown
                                            error={this.state.errors.get('employmentId')}
                                            onChange={async (value) => {
                                                await this.handleChange('employmentId', value);
                                                await this.setState({ employment: undefined });
                                                await this.setEmploymentObject('practitioner');
                                                await this.checkOverlap();
                                            }}
                                            onDoValidate={async () => await this.validateOne('employmentId')}
                                            placeholder={translateKey(
                                                'common.selectItem',
                                                translateKey('employment.employment')
                                            )}
                                            selectOptions={this.state.practitionerEmployments.map(
                                                (practitionerEmployment) => ({
                                                    value: practitionerEmployment.id,
                                                    text: practitionerEmployment.organization.name,
                                                })
                                            )}
                                            value={serviceProviderState.employmentId}
                                        />
                                    </InputGroup>
                                </FormGroup>

                                <FormGroup hasErrors={this.state.errors.has('serviceId')}>
                                    <FormLabel isRequired>{translateKey('service.service')}</FormLabel>
                                    <InputGroup>
                                        <Dropdown
                                            error={this.state.errors.get('serviceId')}
                                            onChange={async (value) => {
                                                await this.handleChange('serviceId', value);
                                                let selectedService: IService | undefined;
                                                if (value) {
                                                    selectedService = this.state.services.find(
                                                        (item) => item.id === value
                                                    );
                                                }
                                                await this.setState(({ serviceProvider }) => ({
                                                    service: selectedService,
                                                    serviceProvider: {
                                                        ...serviceProvider,
                                                        usingSpirometer: false,
                                                        spirometerSoftwareName: null,
                                                        usingRestEcgMeter: false,
                                                    },
                                                }));
                                            }}
                                            onDoValidate={async () => await this.validateOne('serviceId')}
                                            placeholder={translateKey(
                                                'common.selectItem',
                                                translateKey('service.service')
                                            )}
                                            selectOptions={this.state.services.map((serviceItem) => ({
                                                optgroup: serviceItem.serviceType,
                                                text: serviceItem.name,
                                                value: serviceItem.id,
                                            }))}
                                            value={serviceProviderState.serviceId}
                                        />
                                    </InputGroup>
                                </FormGroup>
                            </>
                        )}

                        {!this.isEdit() && this.getQueryServiceId() && (
                            <>
                                <FormHeader>
                                    {translateKey('organization.organization') +
                                        ' & ' +
                                        translateKey('practitioner.practitioner')}
                                </FormHeader>

                                <FormGroup hasErrors={this.state.errors.has('employmentId')}>
                                    <FormLabel isRequired>{translateKey('organization.organization')}</FormLabel>
                                    <InputGroup>
                                        <OrganizationSelection
                                            error={this.state.errors.get('employmentId')}
                                            isKsyosOrganizationExcluded={true}
                                            onChange={async (value) => {
                                                await this.setState({ organizationId: value || null });
                                                await this.loadOrganizationEmployments(value || null);
                                            }}
                                            value={this.state.organizationId ?? undefined}
                                        />
                                    </InputGroup>
                                </FormGroup>

                                {this.state.organizationId && (
                                    <FormGroup hasErrors={this.state.errors.has('employmentId')}>
                                        <FormLabel isRequired>{translateKey('employment.employment')}</FormLabel>
                                        <InputGroup>
                                            <Dropdown
                                                error={this.state.errors.get('employmentId')}
                                                onChange={async (value) => {
                                                    await this.handleChange('employmentId', value);
                                                    await this.setEmploymentObject('organization');
                                                    await this.checkOverlap();
                                                }}
                                                onDoValidate={async () => await this.validateOne('employmentId')}
                                                placeholder={translateKey(
                                                    'common.selectItem',
                                                    translateKey('employment.employment')
                                                )}
                                                selectOptions={this.state.organizationEmployments.map(
                                                    (organizationEmployment) => ({
                                                        text: getDisplayName(organizationEmployment.practitioner),
                                                        value: organizationEmployment.id,
                                                    })
                                                )}
                                                value={serviceProviderState.employmentId}
                                            />
                                        </InputGroup>
                                    </FormGroup>
                                )}
                            </>
                        )}

                        <FormHeader>{translateKey('setting.settings')}</FormHeader>

                        {service && this.isExamination() && (
                            <>
                                <FormGroup hasErrors={this.state.errors.has('isBiometrician')}>
                                    <FormLabel>{translateKey('serviceProvider.isBiometrician')}</FormLabel>
                                    <InputGroup>
                                        <InputCheckbox
                                            checked={serviceProviderState.isBiometrician}
                                            disabled={
                                                this.state.service &&
                                                this.state.service.isStandaloneGraderNotAllowed &&
                                                serviceProviderState.isGrader
                                            }
                                            error={this.state.errors.get('isBiometrician')}
                                            onChange={async (value) => {
                                                await this.handleChange('isBiometrician', value);
                                                if (!value) {
                                                    await this.setState(({ serviceProvider }) => ({
                                                        serviceProvider: {
                                                            ...serviceProvider,
                                                            isGraderSelector: false,
                                                            isRequestCreator: false,
                                                            isServiceConsumerSelector: false,
                                                            canSelectCareProviderAsServiceConsumer: false,
                                                            compensationPayoutProcedure: null,
                                                            compensationPayoutGlaucoma: null,
                                                            usingSpirometer: false,
                                                            spirometerSoftwareName: null,
                                                            usingRestEcgMeter: false,
                                                        },
                                                    }));
                                                }
                                                if (value && serviceProviderState.isAnalyst) {
                                                    await this.handleChange('isAnalyst', false);
                                                }
                                            }}
                                            onDoValidate={async () => await this.validateOne('isBiometrician')}
                                        />
                                    </InputGroup>
                                </FormGroup>

                                {serviceProviderState.isBiometrician && (
                                    <>
                                        <FormGroup hasErrors={this.state.errors.has('isGraderSelector')}>
                                            <FormLabel>{translateKey('serviceProvider.graderSelector')}</FormLabel>
                                            <InputGroup>
                                                <InputCheckbox
                                                    checked={serviceProviderState.isGraderSelector}
                                                    error={this.state.errors.get('isGraderSelector')}
                                                    onChange={async (value) =>
                                                        await this.handleChange('isGraderSelector', value)
                                                    }
                                                    onDoValidate={async () =>
                                                        await this.validateOne('isGraderSelector')
                                                    }
                                                />
                                            </InputGroup>
                                        </FormGroup>
                                        <FormGroup hasErrors={this.state.errors.has('isRequestCreator')}>
                                            <FormLabel>{translateKey('serviceProvider.requestCreator')}</FormLabel>
                                            <InputGroup>
                                                <InputCheckbox
                                                    checked={serviceProviderState.isRequestCreator}
                                                    error={this.state.errors.get('isRequestCreator')}
                                                    onChange={async (value) => {
                                                        await this.handleChange('isRequestCreator', value);
                                                        if (!value) {
                                                            await this.setState(({ serviceProvider }) => ({
                                                                serviceProvider: {
                                                                    ...serviceProvider,
                                                                    isServiceConsumerSelector: false,
                                                                    canSelectCareProviderAsServiceConsumer: false,
                                                                },
                                                            }));
                                                        }
                                                    }}
                                                    onDoValidate={async () =>
                                                        await this.validateOne('isRequestCreator')
                                                    }
                                                />
                                            </InputGroup>
                                        </FormGroup>

                                        {serviceProviderState.isRequestCreator && (
                                            <FormGroup hasErrors={this.state.errors.has('isServiceConsumerSelector')}>
                                                <FormLabel>
                                                    {translateKey('serviceProvider.consumerSelector')}
                                                </FormLabel>
                                                <InputGroup>
                                                    <InputCheckbox
                                                        checked={serviceProviderState.isServiceConsumerSelector}
                                                        error={this.state.errors.get('isServiceConsumerSelector')}
                                                        onChange={async (value) =>
                                                            await this.handleChange('isServiceConsumerSelector', value)
                                                        }
                                                        onDoValidate={async () =>
                                                            await this.validateOne('isServiceConsumerSelector')
                                                        }
                                                    />
                                                </InputGroup>
                                            </FormGroup>
                                        )}
                                        {serviceProviderState.isServiceConsumerSelector && (
                                            <FormGroup
                                                hasErrors={this.state.errors.has(
                                                    'canSelectCareProviderAsServiceConsumer'
                                                )}
                                            >
                                                <FormLabel>
                                                    {translateKey(
                                                        'serviceProvider.canSelectCareProviderAsServiceConsumer'
                                                    )}
                                                </FormLabel>
                                                <InputGroup>
                                                    <InputCheckbox
                                                        checked={
                                                            serviceProviderState.canSelectCareProviderAsServiceConsumer
                                                        }
                                                        error={this.state.errors.get(
                                                            'canSelectCareProviderAsServiceConsumer'
                                                        )}
                                                        onChange={async (value) =>
                                                            await this.handleChange(
                                                                'canSelectCareProviderAsServiceConsumer',
                                                                value
                                                            )
                                                        }
                                                        onDoValidate={async () =>
                                                            await this.validateOne(
                                                                'canSelectCareProviderAsServiceConsumer'
                                                            )
                                                        }
                                                    />
                                                </InputGroup>
                                            </FormGroup>
                                        )}
                                    </>
                                )}

                                <FormGroup hasErrors={this.state.errors.has('isGrader')}>
                                    <FormLabel>{translateKey('serviceProvider.isGrader')}</FormLabel>
                                    <InputGroup>
                                        <InputCheckbox
                                            checked={serviceProviderState.isGrader}
                                            error={this.state.errors.get('isGrader')}
                                            onChange={async (value) => {
                                                await this.handleChange('isGrader', value);
                                                if (!value) {
                                                    await this.setState(({ serviceProvider }) => ({
                                                        serviceProvider: {
                                                            ...serviceProvider,
                                                            compensationPayoutReport: null,
                                                        },
                                                    }));
                                                }
                                                if (value && serviceProviderState.isAnalyst) {
                                                    await this.handleChange('isAnalyst', false);
                                                }
                                                if (
                                                    value &&
                                                    this.state.service &&
                                                    this.state.service.isStandaloneGraderNotAllowed
                                                ) {
                                                    await this.handleChange('isBiometrician', true);
                                                }
                                            }}
                                            onDoValidate={async () => await this.validateOne('isGrader')}
                                        />
                                    </InputGroup>
                                </FormGroup>

                                {this.state.service && getAnalystAllowedByServiceCode(this.state.service.code) && (
                                    <>
                                        <FormGroup hasErrors={this.state.errors.has('isAnalyst')}>
                                            <FormLabel>{translateKey('serviceProvider.isAnalyst')}</FormLabel>
                                            <InputGroup>
                                                <InputCheckbox
                                                    checked={serviceProviderState.isAnalyst}
                                                    error={this.state.errors.get('isAnalyst')}
                                                    onChange={async (value) => {
                                                        await this.handleChange('isAnalyst', value);
                                                        if (value) {
                                                            if (serviceProviderState.isGrader) {
                                                                await this.handleChange('isGrader', false);
                                                            }
                                                            if (serviceProviderState.isBiometrician) {
                                                                await this.handleChange('isBiometrician', false);
                                                            }
                                                        }
                                                    }}
                                                    onDoValidate={async () => await this.validateOne('isAnalyst')}
                                                />
                                            </InputGroup>
                                        </FormGroup>
                                    </>
                                )}
                            </>
                        )}

                        <FormGroup hasErrors={this.state.errors.has('isRequestRecipient')}>
                            <FormLabel>{translateKey('serviceProvider.isRequestRecipient')}</FormLabel>
                            <InputGroup>
                                <InputCheckbox
                                    checked={serviceProviderState.isRequestRecipient}
                                    error={this.state.errors.get('isRequestRecipient')}
                                    onChange={async (value) => await this.handleChange('isRequestRecipient', value)}
                                    onDoValidate={async () => await this.validateOne('isRequestRecipient')}
                                />
                            </InputGroup>
                        </FormGroup>
                        {this.state.showOrganizationNoRecipientMessage && (
                            <Alert appearance="info">
                                {translateKey('serviceProvider.messageOrganizationNoRecipient')}
                            </Alert>
                        )}

                        {serviceProviderState.isBiometrician && service && service.code === ServiceCode.TPO && (
                            <>
                                <FormGroup hasErrors={this.state.errors.has('usingSpirometer')}>
                                    <FormLabel>{translateKey('serviceProvider.usingSpirometer')}</FormLabel>
                                    <InputGroup>
                                        <InputCheckbox
                                            checked={serviceProviderState.usingSpirometer}
                                            error={this.state.errors.get('usingSpirometer')}
                                            onChange={async (value) => {
                                                await this.handleChange('usingSpirometer', value);
                                                if (!value) {
                                                    await this.setState(({ serviceProvider }) => ({
                                                        serviceProvider: {
                                                            ...serviceProvider,
                                                            spirometerSoftwareName: null,
                                                        },
                                                    }));
                                                }
                                            }}
                                            onDoValidate={async () => await this.validateOne('usingSpirometer')}
                                        />
                                    </InputGroup>
                                </FormGroup>

                                {serviceProviderState.usingSpirometer && (
                                    <FormGroup hasErrors={this.state.errors.has('spirometerSoftwareName')}>
                                        <FormLabel isRequired>
                                            {translateKey('serviceProvider.spirometerSoftwareName')}
                                        </FormLabel>
                                        <InputGroup>
                                            <EnumDropdown
                                                enumName={'spirometerSoftwareNames'}
                                                error={this.state.errors.get('spirometerSoftwareName')}
                                                jsEnum={SpirometerSoftwareNames}
                                                onChange={async (value) =>
                                                    await this.handleChange('spirometerSoftwareName', value)
                                                }
                                                onDoValidate={async () =>
                                                    await this.validateOne('spirometerSoftwareName')
                                                }
                                                placeholder={translateKey('serviceProvider.spirometerSoftwareName')}
                                                value={serviceProviderState.spirometerSoftwareName}
                                            />
                                        </InputGroup>
                                    </FormGroup>
                                )}
                            </>
                        )}

                        {serviceProviderState.isBiometrician && service && service.code === ServiceCode.TCOR && (
                            <>
                                <FormGroup hasErrors={this.state.errors.has('usingRestEcgMeter')}>
                                    <FormLabel>{translateKey('serviceProvider.usingRestEcgMeter')}</FormLabel>
                                    <InputGroup>
                                        <InputCheckbox
                                            checked={serviceProviderState.usingRestEcgMeter}
                                            error={this.state.errors.get('usingRestEcgMeter')}
                                            onChange={async (value) => {
                                                await this.handleChange('usingRestEcgMeter', value);
                                                if (!value) {
                                                    await this.setState(({ serviceProvider }) => ({
                                                        serviceProvider: {
                                                            ...serviceProvider,
                                                            restEcgMeterSoftwareName: null,
                                                        },
                                                    }));
                                                }
                                            }}
                                            onDoValidate={async () => await this.validateOne('usingRestEcgMeter')}
                                        />
                                    </InputGroup>
                                </FormGroup>

                                {serviceProviderState.usingRestEcgMeter && (
                                    <FormGroup hasErrors={this.state.errors.has('restEcgMeterSoftwareName')}>
                                        <FormLabel isRequired>
                                            {translateKey('serviceProvider.restEcgMeterSoftwareName')}
                                        </FormLabel>
                                        <InputGroup>
                                            <EnumDropdown
                                                enumName={'restEcgMeterSoftwareNames'}
                                                error={this.state.errors.get('restEcgMeterSoftwareName')}
                                                jsEnum={RestEcgMeterSoftwareNames}
                                                onChange={async (value) => Promise.resolve()}
                                                value={RestEcgMeterSoftwareNames.CardioPerfect}
                                            />
                                        </InputGroup>
                                    </FormGroup>
                                )}
                            </>
                        )}

                        {serviceProviderState.isServiceConsumerSelector &&
                            serviceProviderState.isBiometrician &&
                            serviceProviderState.isRequestCreator &&
                            service &&
                            this.renderServiceOrganizationConsumerListAddEdit(service)}

                        {this.props.router.params.id && (
                            <CompensationPayoutList
                                practitionerId={this.props.router.searchParams.getOptional('practitionerId')}
                                serviceCode={this.state.service?.code}
                                serviceId={this.props.router.searchParams.getOptional('serviceId')}
                                serviceProviderId={this.props.router.params.id}
                                serviceType={this.state.service?.serviceType}
                            />
                        )}

                        <UserTextParagraph>
                            {this.state.showPayoutMessage ? translateKey('serviceProvider.messagePayout') : ''}
                        </UserTextParagraph>

                        {this.state.employment && (
                            <>
                                <FormHeader>{translateKey('common.notifications')}</FormHeader>
                                {this.renderPractitionerNotificationCheckBoxBlock(serviceProviderState)}
                                {this.renderEmploymentNotificationCheckBoxBlock(serviceProviderState)}
                                {this.renderEmploymentAltNotificationCheckBoxBlock(serviceProviderState)}
                            </>
                        )}

                        <CancelButton
                            disabled={this.state.isSaving}
                            to={this.getReturnUrl()}
                        />
                        <SaveSubmit disabled={!!this.state.overlap || this.state.isSaving} />
                    </FormContainer>
                )}
            />
        );
    }

    private renderPractitionerNotificationCheckBoxBlock(serviceProvider: IServiceProvider) {
        const notificationText = translateKey('serviceProvider.sendNotificationPractitioner');
        let email = '';
        if (this.state.practitioner) {
            if (this.state.practitioner.emailAddress) {
                email = this.state.practitioner.emailAddress;
            } else {
                email = translateKey('common.noEmailConfigured');
            }
        } else if (this.state.employment) {
            if (this.state.employment.practitionerEmailAddress) {
                email = this.state.employment.practitionerEmailAddress;
            } else {
                email = translateKey('common.noEmailConfigured');
            }
        }

        return (
            <FormGroup hasErrors={this.state.errors.has('sendNotificationPractitioner')}>
                <FormLabel>{notificationText + `${email ? ' (' + email + ')' : ''}`}</FormLabel>
                <InputGroup>
                    <InputCheckbox
                        checked={serviceProvider.sendNotificationPractitioner}
                        error={this.state.errors.get('sendNotificationPractitioner')}
                        onChange={async (value) => await this.handleChange('sendNotificationPractitioner', value)}
                        onDoValidate={async () => await this.validateOne('sendNotificationPractitioner')}
                    />
                </InputGroup>
            </FormGroup>
        );
    }

    private renderEmploymentNotificationCheckBoxBlock(serviceProvider: IServiceProvider) {
        let message: string;
        if (this.state.employment) {
            if (this.state.employment.emailAddress) {
                message = this.state.employment.emailAddress;
            } else {
                message = translateKey('common.noEmailConfigured');
            }
        } else {
            message = translateKey('serviceProvider.noEmploymentChosen');
        }

        return (
            <FormGroup hasErrors={this.state.errors.has('sendNotificationEmployment')}>
                <FormLabel>
                    {translateKey('serviceProvider.sendNotificationEmployment') +
                        `${message ? ' (' + message + ')' : ''}`}
                </FormLabel>
                <InputGroup>
                    <InputCheckbox
                        checked={serviceProvider.sendNotificationEmployment}
                        error={this.state.errors.get('sendNotificationEmployment')}
                        onChange={async (value) => await this.handleChange('sendNotificationEmployment', value)}
                        onDoValidate={async () => await this.validateOne('sendNotificationEmployment')}
                    />
                </InputGroup>
            </FormGroup>
        );
    }

    private renderEmploymentAltNotificationCheckBoxBlock(serviceProvider: IServiceProvider) {
        let message: string;
        if (this.state.employment) {
            if (this.state.employment.emailAddressAlt) {
                message = this.state.employment.emailAddressAlt;
            } else {
                message = translateKey('common.noEmailConfigured');
            }
        } else {
            message = translateKey('serviceProvider.noEmploymentChosen');
        }

        return (
            <FormGroup hasErrors={this.state.errors.has('sendNotificationEmploymentAlt')}>
                <FormLabel>
                    {translateKey('serviceProvider.sendNotificationEmploymentAlt') +
                        `${message ? ' (' + message + ')' : ''}`}
                </FormLabel>
                <InputGroup>
                    <InputCheckbox
                        checked={serviceProvider.sendNotificationEmploymentAlt}
                        error={this.state.errors.get('sendNotificationEmploymentAlt')}
                        onChange={async (value) => await this.handleChange('sendNotificationEmploymentAlt', value)}
                        onDoValidate={async () => await this.validateOne('sendNotificationEmploymentAlt')}
                    />
                </InputGroup>
            </FormGroup>
        );
    }

    private renderServiceOrganizationConsumerListAddEdit(service: IService) {
        return (
            <>
                {this.state.showServiceConsumerSelectorMessage && (
                    <Alert appearance="info"> {translateKey('serviceProvider.preferredConsumer')}</Alert>
                )}

                <ServiceOrganizationConsumerListAddEdit
                    onChange={async (value: SelectedOrganizations) =>
                        await this.setState({ selectedOrganizations: value })
                    }
                    selectedOrganizations={this.state.selectedOrganizations}
                    service={service}
                />
            </>
        );
    }

    private async validateOne(name: keyof IServiceProvider) {
        await this.setState(({ serviceProvider, errors }) => ({
            errors: updateValidationErrorsForOne(this.getSchema(), serviceProvider, errors, name),
        }));
    }

    private async handleChange<K extends keyof IServiceProvider>(name: K, value: IServiceProvider[K]) {
        await this.setState(({ serviceProvider }) => ({ serviceProvider: { ...serviceProvider, [name]: value } }));

        if (name === 'serviceId' || name === 'startDate' || name === 'endDate') {
            await this.checkOverlap();
        }

        if (name === 'employmentId' || name === 'serviceId' || name === 'isRequestRecipient') {
            await this.checkServiceOrganizationSettings();
        }
    }

    private async checkOverlap() {
        const serviceId = this.getServiceId();
        if (serviceId) {
            this.atMostOncePerSecond.callMeMaybe(async () => {
                if (this.state.serviceProvider && this.state.employment) {
                    const overlap = await this.overlapChecker.check(
                        { employmentId: this.state.employment.id, serviceId: serviceId },
                        this.state.serviceProvider.startDate,
                        this.state.serviceProvider.endDate,
                        this.props.router.params.id
                    );
                    await this.setState({ overlap });
                }
            });
        }
    }

    private async setupComponent(props: ServiceProviderAddEditProps, serviceProviderId: string | null) {
        const practitionerId = this.props.router.searchParams.getOptional('practitionerId');
        const serviceId = this.props.router.searchParams.getOptional('serviceId');
        if (!serviceProviderId) {
            await this.setState({
                serviceProvider: {
                    serviceId: this.getQueryServiceId(),
                    employmentId: null,
                    isGrader: false,
                    isBiometrician: false,
                    isAnalyst: false,
                    isGraderSelector: false,
                    isRequestCreator: false,
                    isRequestRecipient: false,
                    isServiceConsumerSelector: false,
                    canSelectCareProviderAsServiceConsumer: false,
                    startDate: DateTime.now().format('YYYY-MM-DD'),
                    endDate: null,
                    compensationPayoutProcedure: null,
                    compensationPayoutReport: null,
                    compensationPayoutGlaucoma: null,
                    sendNotificationEmploymentAlt: false,
                    sendNotificationEmployment: false,
                    sendNotificationPractitioner: false,
                    usingSpirometer: false,
                    spirometerSoftwareName: null,
                    usingRestEcgMeter: false,
                    preferredOrganizations: [],
                },
            });
        }

        if (practitionerId) {
            await this.loadPractitioner(practitionerId!);
            if (this.isEdit() && serviceProviderId) {
                await this.loadServiceProvider(serviceProviderId);
                await this.loadPreferredServiceOrganizationConsumers(serviceProviderId);
                if (this.state.serviceProvider && this.state.serviceProvider.serviceId) {
                    await this.loadService(this.state.serviceProvider.serviceId);
                }
            } else {
                await this.loadPractitionerEmployments(practitionerId!);
                await this.loadAllServices();
            }
        } else if (serviceId) {
            if (this.isEdit() && serviceProviderId) {
                await this.loadServiceProvider(serviceProviderId);
                await this.loadPreferredServiceOrganizationConsumers(serviceProviderId);
            }
            await this.loadService(serviceId);
        }

        if (this.isEdit() && this.state.serviceProvider && this.state.serviceProvider.employmentId) {
            await this.loadEmployment(this.state.serviceProvider.employmentId);
        }

        await this.setState({ overlap: undefined });
    }

    private async checkServiceOrganizationSettings() {
        if (this.state.employment && this.getServiceId() && this.state.serviceProvider) {
            const organizationId = this.state.employment.organizationId;
            const serviceId = this.getServiceId();
            const url = `/api/serviceOrganizationSettings?organizationId=${organizationId}&serviceId=${serviceId}&isActive=true`;
            const results = await fetchJson(url, 'GET');

            if (results.json.items.length > 0) {
                await this.setState({
                    showPayoutMessage: true,
                    showOrganizationNoRecipientMessage:
                        !this.state.serviceProvider.isRequestRecipient && !results.json.items[0].isRequestRecipient,
                });
            } else {
                await this.setState({
                    showPayoutMessage: false,
                    showOrganizationNoRecipientMessage: !this.state.serviceProvider.isRequestRecipient,
                });
            }
        }
    }

    private async loadServiceProvider(id: string) {
        const url = `/api/serviceProviders/${id}`;
        const result = await fetchJson(url, 'GET');
        await this.setState({ serviceProvider: result.json });
    }

    private async loadPractitioner(practitionerId: string) {
        const url = `/api/practitioners/${practitionerId}`;
        const result = await fetchJson(url, 'GET');
        await this.setState({ practitioner: result.json });
    }

    private async loadEmployment(employmentId: string) {
        const url = `/api/employments/${employmentId}`;
        const result = await fetchJson(url, 'GET');
        await this.setState({ employment: result.json });
    }

    private async loadService(serviceId: string) {
        const url = `/api/services/${serviceId}`;
        const result = await fetchJson(url, 'GET');
        await this.setState({ service: result.json });
    }

    private async loadAllServices() {
        const url = '/api/services?selectionList=true&sort=serviceType';
        const results = await fetchJson(url, 'GET');
        if (results.json.items.length > 1) {
            await this.setState({ services: results.json.items });
        } else if (results.json.items.length === 1) {
            await this.setState(({ serviceProvider }) => ({
                serviceProvider: { ...serviceProvider, serviceId: results.json.items[0].id },
                services: results.json.items,
            }));
        }
    }

    private async loadPreferredServiceOrganizationConsumers(id: string) {
        const url = `/api/preferredServiceOrganizationConsumers?serviceProviderId=${id}`;
        const results = await fetchJson(url, 'GET');
        const selectedOrganizations = Map<string, ISelectedOrganization>(
            results.json.items.map((preferredOrganization: { organization: ISelectedOrganization }) => [
                preferredOrganization.organization.id,
                preferredOrganization.organization,
            ])
        );
        await this.setState({ selectedOrganizations });
    }

    private async loadPractitionerEmployments(practitionerId: string | null) {
        if (practitionerId) {
            const url = `/api/employments/${practitionerId}/organizations?isActive=true&isNoManager=true`;
            const results = await fetchJson(url, 'GET');
            if (results.json.items.length > 1) {
                return await this.setState({ practitionerEmployments: results.json.items });
            } else if (results.json.items.length === 1) {
                await this.setState(({ serviceProvider }) => ({
                    serviceProvider: { ...serviceProvider, employmentId: results.json.items[0].id },
                    practitionerEmployments: results.json.items,
                }));

                return await this.setEmploymentObject('practitioner');
            }
        }

        await this.setState(({ serviceProvider }) => ({
            serviceProvider: { ...serviceProvider, employmentId: null },
            practitionerEmployments: [],
            employment: null,
        }));
    }

    private async loadOrganizationEmployments(organizationId: string | null) {
        if (organizationId) {
            const url = `/api/employments/${organizationId}/practitioners?isActive=true&isNoManager=true`;
            const results = await fetchJson(url, 'GET');
            if (results.json.items.length === 1) {
                await this.setState(({ serviceProvider }) => ({
                    serviceProvider: { ...serviceProvider, employmentId: results.json.items[0].id },
                    organizationEmployments: results.json.items,
                }));
                await this.setEmploymentObject('organization');

                return await this.checkOverlap();
            } else if (results.json.items.length > 1) {
                return await this.setState({ organizationEmployments: results.json.items });
            } else {
                await this.setState(({ serviceProvider }) => ({
                    serviceProvider: { ...serviceProvider, employmentId: null },
                }));
            }
        }

        await this.setState(({ serviceProvider }) => ({
            serviceProvider: { ...serviceProvider, employmentId: null },
            organizationEmployments: [],
            employment: null,
        }));
    }

    private async setEmploymentObject(typeEmployment: 'practitioner' | 'organization') {
        if (this.state.serviceProvider && this.state.serviceProvider.employmentId) {
            let selectedEmployment;
            let organizationId;
            let practitionerEmailAddress;
            if (typeEmployment === 'practitioner' && this.state.practitioner) {
                selectedEmployment = this.state.practitionerEmployments.find(
                    (practitionerEmployment) => practitionerEmployment.id === this.state.serviceProvider!.employmentId
                );
                organizationId = selectedEmployment && selectedEmployment.organizationId;
                practitionerEmailAddress = this.state.practitioner.emailAddress;
            } else if (typeEmployment === 'organization') {
                selectedEmployment = this.state.organizationEmployments.find(
                    (organizationEmployment) => organizationEmployment.id === this.state.serviceProvider!.employmentId
                );
                organizationId = this.state.organizationId;
                practitionerEmailAddress = selectedEmployment && selectedEmployment.practitioner.emailAddress;
            }

            if (selectedEmployment && organizationId && practitionerEmailAddress) {
                const employment: IEmployment = {
                    id: this.state.serviceProvider.employmentId,
                    emailAddress: selectedEmployment.emailAddress,
                    emailAddressAlt: selectedEmployment.emailAddressAlt,
                    organization: {
                        emailAddressRequestInbox: selectedEmployment.organization.emailAddressRequestInbox,
                    },
                    organizationId,
                    practitionerEmailAddress,
                };
                await this.setState({ employment });
                await this.validateOne('employmentId');
            }
        }
    }

    private getReturnUrl() {
        const previousId = this.props.router.searchParams.get('previousId');
        if (previousId) {
            return (
                `/serviceProviders/${previousId}?` +
                `${
                    this.getQueryServiceId() && this.state.serviceProvider && this.state.serviceProvider.serviceId
                        ? `serviceId=${this.getQueryServiceId()}`
                        : ''
                }` +
                `${
                    this.getQueryPractitionerId() && this.state.practitioner
                        ? `practitionerId=${this.getQueryPractitionerId()}`
                        : ''
                }`
            );
        } else if (this.getQueryPractitionerId()) {
            return `/practitioners/${this.getQueryPractitionerId()}?tab=servicesProvidingListIsVisible`;
        } else if (this.getQueryServiceId()) {
            return `/services/${this.getQueryServiceId()}?tab=providers`;
        }

        throw new Error('Unknown return destination');
    }

    private async save() {
        if (!this.state.serviceProvider) {
            return;
        }

        if (this.state.serviceProvider.isServiceConsumerSelector && this.state.selectedOrganizations.size < 1) {
            await this.setState({ showServiceConsumerSelectorMessage: true });

            return;
        } else {
            await this.setState({ showServiceConsumerSelectorMessage: false });
        }

        let url = '/api/serviceProviders';
        if (this.isEdit()) {
            url += '/' + this.getParamServiceProviderId();
        }

        const serviceProvider = {
            ...this.state.serviceProvider,
            preferredOrganizations: this.state.selectedOrganizations
                .toArray()
                .map((prefServOrgCons: ISelectedOrganization) => {
                    if (prefServOrgCons) {
                        return prefServOrgCons.id;
                    }

                    return;
                }),
        };
        const { errors, item } = validate(this.getSchema(), serviceProvider);
        await this.setState({ errors });
        if (errors.size > 0) {
            return;
        }

        await fetchJson(url, 'POST', item);
        this.props.router.navigate(this.getReturnUrl());
    }

    private getSchema() {
        return this.isEdit() ? serviceProviderEditSchema : serviceProviderAddSchema;
    }

    private isExamination() {
        return (this.state.service && this.state.service.serviceType === ServiceType.examination) || false;
    }

    private getServiceId() {
        return this.state.serviceProvider && this.state.serviceProvider.serviceId;
    }

    private getQueryServiceId() {
        return this.props.router.searchParams.get('serviceId');
    }

    private getQueryPractitionerId() {
        return this.props.router.searchParams.get('practitionerId');
    }

    private getParamServiceProviderId() {
        return this.props.router.params.id || null;
    }

    private isEdit() {
        return !!this.getParamServiceProviderId();
    }
}

export const ServiceProviderAddEdit = withRouter(InnerServiceProviderAddEdit);
